import React, { memo, useMemo } from "react";

import useTiCoreApi from "../../../../../ticore/analysis/useTiCoreApi";
import {
  MitreAttackTactic,
  MitreAttackTechnique
} from "../../../../@types/rldynamicanalysis";
import { useGetMergedRlcsReport } from "../../../rlcloudsandbox/hooks/useGetMergedRlcsReport";
import MitreAttackTable from "./MitreAttackTable";
import { icons } from "./consts";

interface Props {
  hash: string;
}

interface MitreResponse {
  attack: { matrix: string; tactics: MitreAttackTactic[] }[];
}
export interface MitreAttackTechniqueWithSource extends MitreAttackTechnique {
  source: "Static" | "Dynamic" | "Static & Dynamic";
}
const responseTransformation = (response: MitreResponse) => {
  const data = response?.attack[0] || [];
  return {
    attack: {
      [data.matrix]: data.tactics
    }
  };
};

const mergeTechniques = (
  staticTechniques: MitreAttackTechniqueWithSource[],
  rlcsTechniques: MitreAttackTechniqueWithSource[]
) => {
  const map = new Map<string, MitreAttackTechniqueWithSource>();

  staticTechniques.forEach(item => {
    map.set(item.id, { ...item, source: "Static" });
  });
  rlcsTechniques.forEach(item => {
    const existingItem = map.get(item.id);
    if (existingItem) {
      map.set(item.id, { ...item, source: "Static & Dynamic" });
    } else {
      map.set(item.id, { ...item, source: "Dynamic" });
    }
  });
  return Array.from(map.values());
};

const mergeObjects = (
  obj1: Record<string, MitreAttackTechniqueWithSource[]>,
  obj2: Record<string, MitreAttackTechniqueWithSource[]>
) =>
  icons
    .map(icon => icon.name)
    .reduce((acc, key) => {
      acc[key] = mergeTechniques(obj1[key] || [], obj2[key] || []);
      return acc;
    }, {} as Record<string, MitreAttackTechniqueWithSource[]>);

const MitreAttack: React.FC<Props> = ({ hash }) => {
  const [response] = useTiCoreApi(hash, "attack");
  const { mergedReportData } = useGetMergedRlcsReport(hash);

  const mappings = useMemo(() => {
    const resp = responseTransformation(response as unknown as MitreResponse);

    const { Enterprise: mitreTactics } = resp.attack || {};
    const { Enterprise: mitreTacticsRlcs } =
      mergedReportData?.mitre_attack || {};

    const rlcsMappings = (mitreTacticsRlcs?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.name]: curr?.techniques.map(tech => ({ ...tech }))
      }),
      {}
    ) || {}) as Record<string, MitreAttackTechniqueWithSource[]>;
    const coreMappings = (mitreTactics?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.name]: curr?.techniques.map(tech => ({ ...tech }))
      }),
      {}
    ) || {}) as Record<string, MitreAttackTechniqueWithSource[]>;

    return mergeObjects(coreMappings, rlcsMappings);
  }, [response, mergedReportData]);

  return <MitreAttackTable mitre={mappings} />;
};

export default memo(MitreAttack);
